import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, Upload, FileText, BarChart3, ChevronDown, ChevronRight, Menu, X, Check, AlertTriangle, Scale, Zap } from 'lucide-react';

const NAV_LINKS = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Templates', href: '/templates' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

function Navbar() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            l.href.startsWith('/') ? (
              <Link key={l.href} to={l.href} className="font-body text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} className="font-body text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors">{l.label}</a>
            )
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <Link to="/dashboard" className="bg-brand text-white hover:bg-brand-hover px-5 py-2 font-body font-medium text-sm transition-colors" data-testid="nav-dashboard-btn">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="font-body text-sm font-medium text-zinc-700 hover:text-zinc-900 px-4 py-2 transition-colors" data-testid="nav-login-btn">Log In</Link>
              <Link to="/signup" className="bg-brand text-white hover:bg-brand-hover px-5 py-2 font-body font-medium text-sm transition-colors" data-testid="nav-signup-btn">Get Started</Link>
            </>
          )}
        </div>
        <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {menuOpen && (
        <div className="md:hidden border-t border-zinc-200 bg-white px-6 py-4">
          {NAV_LINKS.map(l => (
            l.href.startsWith('/') ? (
              <Link key={l.href} to={l.href} onClick={() => setMenuOpen(false)} className="block py-2 font-body text-sm text-zinc-600">{l.label}</Link>
            ) : (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="block py-2 font-body text-sm text-zinc-600">{l.label}</a>
            )
          ))}
          <div className="flex gap-3 mt-4">
            <Link to="/login" className="flex-1 text-center border border-zinc-200 py-2 text-sm font-medium" data-testid="mobile-login-btn">Log In</Link>
            <Link to="/signup" className="flex-1 text-center bg-brand text-white py-2 text-sm font-medium" data-testid="mobile-signup-btn">Get Started</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="pt-28 pb-20 px-6 md:px-12 lg:px-24 bg-white relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #09090B 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center relative">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-4">Contract Risk Intelligence</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-zinc-900 leading-[1.05]">
            Understand contract risks <span className="text-brand">before you sign</span>
          </h1>
          <p className="mt-6 font-body text-lg text-zinc-600 leading-relaxed max-w-xl">
            Upload a contract and get a plain-English breakdown of risky clauses, obligations, and negotiation points in minutes.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button onClick={() => navigate('/signup')} className="bg-brand text-white hover:bg-brand-hover px-8 py-3 font-body font-medium transition-colors flex items-center gap-2" data-testid="hero-get-started-btn">
              Get Started Free <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate('/demo')} className="bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 px-8 py-3 font-body font-medium transition-colors" data-testid="hero-try-demo-btn">
              Try Demo
            </button>
          </div>
          <p className="mt-4 font-body text-xs text-zinc-400">No credit card required. 2 free scans per month.</p>
        </div>
        <div className="hidden lg:block">
          <div className="border border-zinc-200 bg-zinc-50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-rose-500" />
              <span className="font-mono text-sm font-medium text-zinc-700">Risk Score: 7/10</span>
            </div>
            <div className="space-y-3">
              <div className="border border-zinc-200 bg-white p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">High</span>
                  <span className="font-body text-sm font-medium text-zinc-800">Non-Compete</span>
                </div>
                <p className="font-body text-xs text-zinc-500">24-month nationwide restriction is overly broad...</p>
              </div>
              <div className="border border-zinc-200 bg-white p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Medium</span>
                  <span className="font-body text-sm font-medium text-zinc-800">Payment Terms</span>
                </div>
                <p className="font-body text-xs text-zinc-500">45-day payment window with no late interest...</p>
              </div>
              <div className="border border-zinc-200 bg-white p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Low</span>
                  <span className="font-body text-sm font-medium text-zinc-800">Dispute Resolution</span>
                </div>
                <p className="font-body text-xs text-zinc-500">Mandatory arbitration with jury trial waiver...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { icon: Upload, title: 'Upload Your Contract', desc: 'Drag and drop a PDF or DOCX file, or paste contract text directly.' },
    { icon: Zap, title: 'AI-Powered Analysis', desc: 'Our AI reviews every clause and identifies risks, obligations, and red flags.' },
    { icon: BarChart3, title: 'Get Risk Report', desc: 'Receive a plain-English breakdown with risk scores and suggested revisions.' },
  ];
  return (
    <section id="how-it-works" className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-zinc-50 border-y border-zinc-200" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">How It Works</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-12">Three steps to contract clarity</h2>
        <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {steps.map((s, i) => (
            <div key={i} className="bg-white p-8 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-sm text-zinc-400 font-medium">0{i + 1}</span>
                <s.icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2 text-zinc-900">{s.title}</h3>
              <p className="font-body text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    { icon: AlertTriangle, title: 'Risk Flags', desc: 'Red flags categorized by severity with specific clause references.' },
    { icon: Scale, title: 'Legal Categories', desc: 'Payment terms, termination, liability, IP, non-compete, and more.' },
    { icon: FileText, title: 'Plain English', desc: 'No legalese. Every finding explained in language you understand.' },
    { icon: BarChart3, title: 'Risk Score', desc: 'A clear 1-10 risk score for quick contract assessment.' },
    { icon: Eye, title: 'Revision Suggestions', desc: 'Actionable suggestions for safer contract language.' },
    { icon: Zap, title: 'Fast Analysis', desc: 'Get comprehensive results in under 2 minutes.' },
  ];
  return (
    <section id="features" className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-white" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">Features</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-12">Everything you need to review a contract</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
          {features.map((f, i) => (
            <div key={i} className="bg-white p-6">
              <f.icon className="w-5 h-5 text-brand mb-3" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-base mb-1 text-zinc-900">{f.title}</h3>
              <p className="font-body text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  return (
    <section id="pricing" className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-zinc-50 border-y border-zinc-200" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">Pricing</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-6">Simple, transparent pricing</h2>

        <div className="flex items-center justify-center gap-3 mb-10" data-testid="billing-toggle">
          <span className={`font-body text-sm font-medium ${!annual ? 'text-zinc-900' : 'text-zinc-400'}`}>Monthly</span>
          <button onClick={() => setAnnual(!annual)} className={`relative w-12 h-6 transition-colors ${annual ? 'bg-brand' : 'bg-zinc-300'}`} data-testid="billing-toggle-btn">
            <span className={`absolute top-0.5 w-5 h-5 bg-white transition-transform ${annual ? 'left-6' : 'left-0.5'}`} />
          </button>
          <span className={`font-body text-sm font-medium ${annual ? 'text-zinc-900' : 'text-zinc-400'}`}>Annual</span>
          {annual && <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Save 20%</span>}
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl">
          <div className="bg-white border border-zinc-200 p-8" data-testid="pricing-free-card">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-2">Free</p>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="font-heading font-black text-4xl text-zinc-900">$0</span>
              <span className="font-body text-sm text-zinc-500">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['2 contract scans per month', 'Full risk analysis', 'Risk score & flags', 'Suggested revisions', 'Analysis history'].map((f, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm text-zinc-700">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/signup')} className="w-full border border-zinc-200 text-zinc-900 hover:bg-zinc-50 py-3 font-body font-medium text-sm transition-colors" data-testid="pricing-free-btn">
              Start Free
            </button>
          </div>
          <div className="bg-white border border-zinc-200 p-8" data-testid="pricing-pro-card">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-2">Pro</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-heading font-black text-4xl text-zinc-900">${annual ? '8' : '10'}</span>
              <span className="font-body text-sm text-zinc-500">/month</span>
            </div>
            {annual && <p className="font-body text-xs text-zinc-400 mb-3">$96 billed annually</p>}
            {!annual && <div className="mb-4" />}
            <ul className="space-y-3 mb-8">
              {['50 contract scans per month', 'Full risk analysis', 'Risk score & flags', 'Suggested revisions', 'Analysis history', 'Priority processing'].map((f, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm text-zinc-700">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/signup')} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-3 font-body font-medium text-sm transition-colors" data-testid="pricing-pro-btn">
              Get Pro
            </button>
          </div>
          <div className="bg-white border-2 border-brand p-8 relative" data-testid="pricing-pro-plus-card">
            <div className="absolute -top-3 left-8 bg-brand text-white px-3 py-1 text-xs font-bold tracking-wider uppercase font-body">Best Value</div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand font-body mb-2">Pro Plus</p>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="font-heading font-black text-4xl text-zinc-900">${annual ? '12' : '15'}</span>
              <span className="font-body text-sm text-zinc-500">/month</span>
            </div>
            {annual && <p className="font-body text-xs text-zinc-400 mb-3">$144 billed annually</p>}
            {!annual && <div className="mb-4" />}
            <ul className="space-y-3 mb-8">
              {['50 contract scans per month', 'Everything in Pro', 'Contract comparison mode', 'AI negotiation emails', 'PDF report downloads', 'Side-by-side risk diff'].map((f, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-sm text-zinc-700">
                  <Check className="w-4 h-4 text-brand mt-0.5 shrink-0" strokeWidth={2} />
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/signup')} className="w-full bg-brand text-white hover:bg-brand-hover py-3 font-body font-medium text-sm transition-colors" data-testid="pricing-pro-plus-btn">
              Get Pro Plus
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  const faqs = [
    { q: 'What types of contracts can I upload?', a: 'You can upload PDF or DOCX files, or paste contract text directly. We support freelance agreements, NDAs, service contracts, employment agreements, and most common business contracts.' },
    { q: 'Is this legal advice?', a: 'No. ContractEye provides risk insights, possible concerns, and plain-English explanations to help you understand your contracts better. It does not replace professional legal counsel. Always consult a qualified attorney for legal advice.' },
    { q: 'How accurate is the analysis?', a: 'Our AI analysis is powered by advanced language models and identifies common risk patterns, obligations, and red flags. While highly useful for initial review, we recommend professional legal review for critical contracts.' },
    { q: 'Is my contract data secure?', a: 'Yes. Your contracts are encrypted in transit and at rest. We do not share your contract data with third parties. Analysis data is stored securely and only accessible to your account.' },
    { q: 'Can I cancel my subscription?', a: 'Yes, you can cancel your Pro subscription anytime from the Settings page. You will retain Pro access until the end of your billing period.' },
    { q: 'What happens when I hit my scan limit?', a: 'Free users get 2 scans per month. When you reach your limit, you can upgrade to Pro for 50 scans per month. Usage resets on the first of each month.' },
  ];
  return (
    <section id="faq" className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-3">FAQ</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-12">Frequently asked questions</h2>
        <div className="border border-zinc-200 divide-y divide-zinc-200">
          {faqs.map((f, i) => (
            <div key={i}>
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <span className="font-body font-medium text-sm text-zinc-900 pr-4">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
              </button>
              {openIdx === i && (
                <div className="px-6 pb-4">
                  <p className="font-body text-sm text-zinc-600 leading-relaxed">{f.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-zinc-200 bg-zinc-50 py-12 px-6 md:px-12 lg:px-24" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-brand" strokeWidth={2} />
            <span className="font-heading font-black text-base text-zinc-900">ContractEye</span>
          </div>
          <div className="flex gap-6">
            {NAV_LINKS.map(l => (
              l.href.startsWith('/') ? (
                <Link key={l.href} to={l.href} className="font-body text-xs text-zinc-500 hover:text-zinc-700">{l.label}</Link>
              ) : (
                <a key={l.href} href={l.href} className="font-body text-xs text-zinc-500 hover:text-zinc-700">{l.label}</a>
              )
            ))}
          </div>
        </div>
        <div className="border-t border-zinc-200 pt-6">
          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6" data-testid="footer-disclaimer">
            <p className="font-body text-xs text-amber-800 leading-relaxed">
              <strong>Disclaimer:</strong> ContractEye provides risk insights and plain-English explanations for informational purposes only. This tool does not provide legal advice and is not a substitute for consultation with a qualified attorney. Always seek professional legal counsel for important contract decisions.
            </p>
          </div>
          <p className="font-body text-xs text-zinc-400">&copy; {new Date().getFullYear()} ContractEye. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />
      <HeroSection />
      <HowItWorksSection />
      <FeaturesSection />
      <PricingSection />
      <FAQSection />
      <Footer />
    </div>
  );
}
